import { useState, useEffect, useRef, useCallback } from 'react'
import { searchApi, type ApiGlobalSearchResult } from '../utils/api'

// ── §91+§92+§93: Global Search Modal ─────────────────────────────────────────

const TYPE_ICONS: Record<string, string> = {
  site:  '🌐',
  page:  '📄',
  cms:   '🗃',
  media: '🖼',
}

const TYPE_LABELS: Record<string, string> = {
  site:  'Сайт',
  page:  'Страница',
  cms:   'CMS',
  media: 'Медиа',
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debouncedValue
}

interface Props {
  onClose: () => void
  onNavigate?: (result: ApiGlobalSearchResult) => void
  siteId?: string
}

export function GlobalSearchModal({ onClose, onNavigate, siteId }: Props) {
  const [query, setQuery]       = useState('')
  const [results, setResults]   = useState<ApiGlobalSearchResult[]>([])
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState<string | null>(null)
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const debouncedQuery = useDebounce(query, 300)

  useEffect(() => { inputRef.current?.focus() }, [])

  useEffect(() => {
    if (!debouncedQuery.trim() || debouncedQuery.length < 2) {
      setResults([])
      setError(null)
      return
    }
    setLoading(true)
    searchApi.global(debouncedQuery.trim(), siteId)
      .then(r => { setResults(r); setActiveIdx(0); setError(null) })
      .catch(() => setError('Ошибка поиска'))
      .finally(() => setLoading(false))
  }, [debouncedQuery, siteId])

  const handleSelect = useCallback((result: ApiGlobalSearchResult) => {
    onNavigate?.(result)
    onClose()
  }, [onNavigate, onClose])

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { onClose(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); return }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); return }
    if (e.key === 'Enter' && results[activeIdx]) { handleSelect(results[activeIdx]); return }
  }

  const grouped = results.reduce<Record<string, ApiGlobalSearchResult[]>>((acc, r) => {
    (acc[r.type] ??= []).push(r)
    return acc
  }, {})

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10001, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '80px 20px 20px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: 'var(--bg-1)', borderRadius: 16, width: '100%', maxWidth: 560,
        boxShadow: '0 24px 80px rgba(0,0,0,0.3)', overflow: 'hidden',
        border: '1px solid var(--border)', fontFamily: 'system-ui, sans-serif',
      }}>
        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 18, flexShrink: 0, opacity: 0.5 }}>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Поиск по сайтам, страницам, CMS..."
            style={{
              flex: 1, border: 'none', background: 'transparent', fontSize: 16,
              color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit',
            }}
          />
          {loading && <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>...</span>}
          <kbd style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px', flexShrink: 0, cursor: 'pointer' }} onClick={onClose}>Esc</kbd>
        </div>

        {/* Results */}
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {!query.trim() && (
            <div style={{ padding: '32px 16px', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
              Введите запрос для поиска
            </div>
          )}

          {error && (
            <div style={{ padding: '20px 16px', textAlign: 'center', fontSize: 13, color: '#ef4444' }}>
              {error}
            </div>
          )}

          {!loading && query.trim().length >= 2 && results.length === 0 && !error && (
            <div style={{ padding: '32px 16px', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
              Ничего не найдено по «{query}»
            </div>
          )}

          {Object.entries(grouped).map(([type, items]) => (
            <div key={type}>
              <div style={{ padding: '8px 16px 4px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {TYPE_LABELS[type] ?? type}
              </div>
              {items.map((result) => {
                const globalIdx = results.indexOf(result)
                const isActive = globalIdx === activeIdx
                return (
                  <div
                    key={result.id}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setActiveIdx(globalIdx)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 16px', cursor: 'pointer',
                      background: isActive ? 'var(--bg-2)' : 'transparent',
                      transition: 'background 0.1s',
                    }}
                  >
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{TYPE_ICONS[result.type] ?? '📄'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {result.title}
                      </div>
                      {result.subtitle && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {result.subtitle}
                        </div>
                      )}
                    </div>
                    {isActive && (
                      <kbd style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px', flexShrink: 0 }}>↵</kbd>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-muted)' }}>
          <span>↑↓ навигация</span>
          <span>↵ перейти</span>
          <span>Esc закрыть</span>
        </div>
      </div>
    </div>
  )
}
